import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import axios from 'axios';
import { path } from 'ramda';

import IProduct, { IAiFieldReview, IAiReview, IAiReviewRunSummary } from 'models/product';
import { IFoodlaCategoryOption } from 'models/category';
import { filterProductForAIReview } from './utils/aiReviewFields';
import useLayoutContext from 'hooks/useLayoutContext';

import Fab from '@material-ui/core/Fab';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import Collapse from '@material-ui/core/Collapse';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import SmartToyIcon from '@material-ui/icons/Toys'; // Using Toys as a substitute for SmartToy

import { KeycloakContext } from 'components/Secured';
import { APPLICATION_ROLES } from 'components/constants';
import AiReviewRunDialog from './AiReviewRunDialog';
import ConfidenceChip from './ConfidenceChip';
import AiReviewReasonPoints from './AiReviewReasonPoints';
import { AiFieldVerdictContext, IAiFieldVerdict, LEGACY_REVIEW_HINT } from './AiFieldVerdict';
import { describeReviewedValue } from './aiReviewChanges';
import { renderReasonWithSourceChips } from './aiReviewReason';
import { AiReviewFieldHistoryIcon, AiReviewFieldHistoryLoader, TAiReviewFieldHistory } from './AiReviewFieldHistory';
import restEndpoints from 'constants/restEndpoints';
import DynamicText from 'components/DynamicText';

const DRAWER_WIDTH = 350;

// The product and the form keep the review without the model's confidence; superusers see it from the run.
const withoutConfidence = (review: IAiReview | undefined): IAiReview | undefined =>
  review && {
    ...review,
    fieldReviews: review.fieldReviews?.map(({ confidence, ...fieldReview }) => fieldReview),
  };

// Loading messages that rotate during AI analysis
const LOADING_MESSAGES = [
  'Analyserar produktinformation...',
  'Bestämmer produkttyp utifrån kategori...',
  'Söker efter relevanta EU-förordningar...',
  'Hämtar aktuella märkningskrav...',
  'Granskar produktnamn och beskrivning...',
  'Kontrollerar varumärke...',
  'Validerar nettokvantitet...',
  'Analyserar produktinnehåll...',
  'Granskar innehållsförteckning...',
  'Kontrollerar förvaringsanvisning...',
  'Granskar ursprungsmärkning...',
  'Analyserar märkningar och certifieringar...',
  'Kontrollerar kategorispecifika fält...',
  'Validerar produktspecifikationer...',
  'Söker på EUR-Lex...',
  'Söker efter myndighetsriktlinjer...',
  'Kontrollerar skattesats...',
  'Letar efter motsägelser mellan fält...',
  'Kontrollerar obligatoriska fält...',
  'Verifierar datakvalitet...',
  'Granskar säkerhetsinformation...',
  'Kontrollerar användningsinstruktioner...',
  'Korsvaliderar fälten...',
  'Sammanställer granskningsresultat...',
  'Slutför granskning...',
];

const useStyles = makeStyles(theme => ({
  fab: {
    position: 'fixed',
    bottom: theme.spacing(4),
    right: theme.spacing(4),
    zIndex: 1300,
    color: theme.palette.common.white,
    width: 60,
    height: 60,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  sidebar: {
    position: 'fixed',
    right: 0,
    top: 0, // Will be adjusted dynamically
    width: DRAWER_WIDTH,
    backgroundColor: theme.palette.background.paper,
    height: '100%',
    boxShadow: theme.shadows[4],
    zIndex: 1000,
    transform: 'translateX(100%)',
    transition: 'transform 0.3s ease-in-out',
    display: 'flex',
    flexDirection: 'column',
  },
  sidebarOpen: {
    transform: 'translateX(0)',
  },
  sidebarHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    justifyContent: 'space-between',
  },
  productInfo: {
    padding: theme.spacing(1, 2),
    paddingBottom: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  productTitle: {
    fontWeight: 600,
    fontSize: '0.938rem',
  },
  productCategory: {
    fontSize: '0.813rem',
    color: theme.palette.text.secondary,
  },
  refreshButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
  },
  refreshIcon: {
    marginRight: theme.spacing(1),
  },
  aiContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1.5),
    overflowY: 'auto',
    flex: 1,
    paddingBottom: theme.spacing(12), // Add extra padding at the bottom to prevent FAB overlap
  },
  section: {
    marginBottom: theme.spacing(1.5),
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: '#FAFAFA',
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#F5F5F5',
      borderColor: theme.palette.grey[400],
    },
  },
  sectionHeaderFailed: {
    borderLeft: `3px solid ${theme.palette.error.main}`,
  },
  sectionHeaderApproved: {
    borderLeft: `3px solid ${theme.palette.success.main}`,
  },
  sectionHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  expandIcon: {
    color: theme.palette.text.secondary,
    transition: 'transform 0.2s ease',
  },
  fieldReviewItem: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
  },
  fieldReviewApproved: {
    borderLeft: `4px solid ${theme.palette.success.main}`,
  },
  fieldReviewFailed: {
    borderLeft: `4px solid ${theme.palette.error.main}`,
  },
  fieldReviewContent: {
    flex: 1,
    minWidth: 0,
  },
  fieldLabelRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  reviewError: {
    margin: theme.spacing(1, 0),
    padding: theme.spacing(0.75, 1.25),
    borderLeft: `3px solid ${theme.palette.error.main}`,
    backgroundColor: 'rgba(244, 67, 54, 0.08)',
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.error.dark,
  },
  legacyNote: {
    margin: theme.spacing(1, 0),
    padding: theme.spacing(0.75, 1.25),
    borderLeft: '3px solid #7b1fa2',
    backgroundColor: 'rgba(123, 31, 162, 0.06)',
    borderRadius: theme.shape.borderRadius,
  },
  changeChip: {
    height: 20,
    marginTop: theme.spacing(0.5),
    fontSize: '0.75rem',
    borderColor: '#7b1fa2',
    color: '#7b1fa2',
    cursor: 'help',
  },
  confidenceChip: {
    height: 20,
    flexShrink: 0,
    fontSize: '0.75rem',
  },
  fieldLabel: {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    lineHeight: 1.4,
  },
  // The product field the verdict is about, for superusers who look for it in the form or the data;
  // on a line of its own under the label, as the panel is narrow.
  fieldKey: {
    lineHeight: 1.3,
    overflowWrap: 'anywhere',
  },
  fieldReason: {
    fontSize: '0.813rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.75),
    lineHeight: 1.5,
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-line',
  },
  fieldReasonPoints: {
    whiteSpace: 'normal',
  },
  sourceChip: {
    maxWidth: 'calc(100% - 8px)',
    height: 22,
    margin: theme.spacing(0, 0.5),
    verticalAlign: 'middle',
    textDecoration: 'none',
    '& .MuiChip-label': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  summaryBox: {
    display: 'flex',
    justifyContent: 'space-around',
    padding: theme.spacing(2, 0),
    marginBottom: theme.spacing(3),
  },
  summaryItem: {
    textAlign: 'center' as const,
    flex: 1,
  },
  summaryCount: {
    fontSize: '1.75rem',
    fontWeight: 700,
    lineHeight: 1.2,
  },
  summaryLabel: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  disclaimer: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 999,
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 0.3s ease',
  },
  overlayVisible: {
    opacity: 1,
    visibility: 'visible',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  loadingSubtext: {
    fontSize: '0.813rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(2),
  },
}));

interface AIReviewerProps {
  state: IProduct;
  userRole: string;
  setState: React.Dispatch<React.SetStateAction<IProduct>>;
  isCreatingNew?: boolean;
  rootCategory?: IFoodlaCategoryOption;
  // The fields sent depend on the category tree, so a review waits until it is loaded.
  categoriesLoading?: boolean;
}

const AIReviewer: React.FC<AIReviewerProps> = ({ state, userRole, setState, rootCategory, categoriesLoading = false }) => {
  const keycloakCtx = useContext(KeycloakContext);
  // Failed fields with whether they changed since the review; filled for superusers only.
  const fieldVerdicts = useContext(AiFieldVerdictContext);
  const token = path(['keycloak', 'token'], keycloakCtx) as string;
  const classes = useStyles();
  const theme = useTheme();
  const { setRightbarWidth } = useLayoutContext();

  const keepRef = useRef({ state });
  keepRef.current.state = state;

  const [open, setOpen] = useState(false);
  const [appBarHeight, setAppBarHeight] = useState(64); // Default height
  const [isCalled, setIsCalled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isError, setIsError] = useState(false);
  // The server's own message, e.g. for too many reviews in a while or a product of someone else.
  const [errorText, setErrorText] = useState<string | null>(null);
  const [aiReviewData, setAiReviewData] = useState(state.aiReview);
  // The stored run of the shown review; the server gives it to superusers only.
  const [aiReviewRunId, setAiReviewRunId] = useState(state.aiReviewRunId || null);
  // The run the details dialog opens on; null while it is closed.
  const [detailsRunId, setDetailsRunId] = useState<string | null>(null);
  // How each field was judged in the past runs; loaded for superusers only.
  const [fieldHistory, setFieldHistory] = useState<TAiReviewFieldHistory>({});
  const [runSummary, setRunSummary] = useState<Pick<IAiReviewRunSummary, 'provider' | 'confidences' | 'reviewedValues'> | null>(
    state.aiReviewRunSummary || null
  );
  const isSuperuser = userRole === APPLICATION_ROLES.SUPERUSER;
  const [failedExpanded, setFailedExpanded] = useState(true);
  const [approvedExpanded, setApprovedExpanded] = useState(false);

  // Abort controller ref for cleanup
  const abortControllerRef = useRef<AbortController | null>(null);

  // Media query for responsive behavior
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')); // 'md' is typically 960px

  const handleClose = () => {
    setOpen(false);
  };

  // How the review was started, stored with its run: on first opening the panel or with the button.
  const handleLoadAIAnalysis = useCallback(async (trigger: 'auto' | 'button') => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsRefreshing(true);
    setIsError(false); // Reset error state on new request
    setErrorText(null);

    try {
      const formData = new FormData();
      const filteredProduct = filterProductForAIReview(keepRef.current.state, rootCategory);
      formData.append('product', JSON.stringify(filteredProduct));
      formData.append('trigger', trigger);
      // For the run log only: the model gets the category name, and names repeat under different roots.
      const categoryId = keepRef.current.state?.foodlaCategory?.id;
      if (categoryId) formData.append('categoryId', categoryId);

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.forEach((value, key) => {
        if ((['pro', 'flash', 'fallback'].includes(key) && ['1', 'true'].includes(value)) || key === 'temperature') {
          formData.append(key, value);
        }
      });

      const { data } = await axios.post<{ data: IAiReview; runId?: string; provider?: string; model?: string }>(
        restEndpoints.aiProductReview,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: abortControllerRef.current.signal,
        }
      );

      // Only superusers get the run, its provider and the confidence per field.
      const confidences = Object.fromEntries(
        (data?.data?.fieldReviews || [])
          .filter(review => typeof review.confidence === 'number')
          .map(review => [review.field, review.confidence as number])
      );
      // What the review saw of the fields it failed, to tell later whether they changed since.
      const reviewedValues = Object.fromEntries(
        (data?.data?.fieldReviews || [])
          .filter(review => review.status === 'failed' && Object.prototype.hasOwnProperty.call(filteredProduct, review.field))
          .map(review => [review.field, filteredProduct[review.field] ?? null])
      );
      const summary = data?.provider ? { provider: data.provider, confidences, reviewedValues } : null;
      const review = withoutConfidence(data?.data);
      setAiReviewData(review);
      setAiReviewRunId(data?.runId || null);
      setRunSummary(summary);
      setState({
        ...keepRef.current.state,
        aiReview: review,
        aiReviewRunId: data?.runId || null,
        aiReviewRunSummary: summary && data?.runId ? { id: data.runId, model: data.model || '', ...summary } : null,
      });
    } catch (error: any) {
      // Don't set error state if request was cancelled
      if (error?.name !== 'CanceledError' && error?.code !== 'ERR_CANCELED') {
        console.error('Product review error:', error);
        setIsError(true);
        const serverError = error?.response?.data?.error;
        setErrorText(typeof serverError === 'string' ? serverError : null);
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [token, setState, rootCategory]);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Find the app bar height and adjust the sidebar position
  useEffect(() => {
    const appBar = document.querySelector('header') || document.querySelector('.MuiAppBar-root');
    if (appBar) {
      setAppBarHeight(appBar.clientHeight);
    }
  }, []);

  useEffect(() => {
    // Handle loading state when first opening sidebar
    // Without the category tree a food product is sent with the base fields only.
    if (open && !isCalled && !categoriesLoading && !keepRef.current.state?.aiReview) {
      setIsLoading(true);
      setIsCalled(true);
      handleLoadAIAnalysis('auto').then(() => {
        setIsLoading(false);
      });
    }
  }, [open, isCalled, categoriesLoading, handleLoadAIAnalysis]);

  // Apply the shift effect based on desktop vs mobile
  useEffect(() => {
    if (open && isDesktop) {
      setRightbarWidth(DRAWER_WIDTH);
    } else {
      setRightbarWidth(0);
    }
  }, [open, isDesktop, setRightbarWidth]);

  // Cleanup rightbar width on unmount only
  useEffect(() => {
    return () => setRightbarWidth(0);
  }, [setRightbarWidth]);

  // Memoize filtered field reviews to avoid repeated filtering
  const failedReviews = useMemo(
    () => aiReviewData?.fieldReviews?.filter(r => r.status === 'failed') || [],
    [aiReviewData?.fieldReviews]
  );

  const approvedReviews = useMemo(
    () => aiReviewData?.fieldReviews?.filter(r => r.status === 'approved') || [],
    [aiReviewData?.fieldReviews]
  );

  // Superusers see the model's own confidence per field; it is not calibrated, so others do not.
  const renderFieldLabel = (review: IAiFieldReview) => {
    const confidence = runSummary?.confidences?.[review.field];
    const history = fieldHistory[review.field] || [];
    // Only when an earlier run judged this field too.
    const hasEarlier = history.some(entry => entry.runId !== aiReviewRunId);
    return (
      <div className={classes.fieldLabelRow}>
        <Box display="flex" alignItems="center" minWidth={0}>
          {isSuperuser && hasEarlier && <AiReviewFieldHistoryIcon entries={history} currentRunId={aiReviewRunId} />}
          <Box minWidth={0}>
            <Typography className={classes.fieldLabel}>{review.fieldLabel}</Typography>
            {isSuperuser && (
              <Typography variant="caption" color="textSecondary" component="div" className={classes.fieldKey} data-testid="ai-review-field-key">
                {review.field}
              </Typography>
            )}
            {isSuperuser && review.status === 'failed' && renderChangeNote(fieldVerdicts[review.field])}
          </Box>
        </Box>
        {isSuperuser && typeof confidence === 'number' && (
          <ConfidenceChip confidence={confidence} provider={runSummary?.provider} className={classes.confidenceChip} />
        )}
      </div>
    );
  };

  // A failed field changed since the review: the verdict is about the value the review saw.
  const renderChangeNote = (verdict?: IAiFieldVerdict) => {
    if (!verdict?.change) return null;
    const saved = verdict.change === 'saved';
    const hint =
      (saved
        ? 'The saved value differs from the one this review saw, so the verdict is no longer shown under the field.'
        : 'Changed in the form after this review, not saved yet; the verdict under the field is struck through.') +
      (verdict.legacy ? '' : ` The review saw: “${describeReviewedValue(verdict.reviewedValue)}”.`) +
      ' The AI has not checked the new value; review the product again to check it.';
    return (
      <Tooltip arrow enterTouchDelay={0} title={hint}>
        <Chip
          size="small"
          variant="outlined"
          className={classes.changeChip}
          label={saved ? 'Changed after the review' : 'Changed, not saved'}
          data-testid="ai-review-field-changed"
          data-change={verdict.change}
        />
      </Tooltip>
    );
  };

  // Superusers see the reason a point per line with the finding of the point; others the plain reason.
  const renderFieldReason = (review: IAiFieldReview) =>
    isSuperuser ? (
      <Typography component="div" className={`${classes.fieldReason} ${classes.fieldReasonPoints}`}>
        <AiReviewReasonPoints reason={review.reason} findings={review.findings} />
      </Typography>
    ) : (
      <Typography className={classes.fieldReason}>
        {renderReasonWithSourceChips(review.reason || '', classes.sourceChip)}
      </Typography>
    );

  // Keyboard handler for expandable sections
  const handleKeyDown = useCallback((e: React.KeyboardEvent, toggleFn: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleFn();
    }
  }, []);

  return (
    <>
      {/* Dark overlay for mobile view */}
      {!isDesktop && (
        <div className={`${classes.overlay} ${open ? classes.overlayVisible : ''}`} onClick={handleClose} />
      )}

      <Fab className={classes.fab} color="primary" aria-label="AI Reviewer" onClick={() => setOpen(!open)}>
        {open ? <CloseIcon /> : <SmartToyIcon />}
      </Fab>

      <Box
        className={`${classes.sidebar} ${open ? classes.sidebarOpen : ''}`}
        style={{ top: `${appBarHeight}px`, height: `calc(100% - ${appBarHeight}px)` }}
      >
        <div className={classes.sidebarHeader}>
          <Typography variant="h6">AI Produktgranskning</Typography>
          <IconButton onClick={handleClose} edge="end" aria-label="Stäng">
            <CloseIcon />
          </IconButton>
        </div>

        <div className={classes.productInfo}>
          <Typography className={classes.productTitle}>{state?.title || 'Ingen titel'}</Typography>
          <Typography className={classes.productCategory}>{state?.foodlaCategory?.name || 'Ingen kategori'}</Typography>
        </div>

        <div className={classes.aiContent} style={{ display: isLoading ? 'none' : undefined }}>
          <div className={classes.disclaimer}>
            <Typography variant="caption">
              Detta är en experimentell funktion. Kontrollera och granska svaren.
            </Typography>
          </div>
          {/* A refused or failed new review, above the review the product already has. */}
          {isError && !!aiReviewData && (
            <div className={classes.reviewError} role="alert" data-testid="ai-review-error">
              <Typography variant="caption">
                {errorText || 'Det gick inte att hämta en ny AI-analys. Nedan visas den tidigare analysen.'}
              </Typography>
            </div>
          )}
          {/* Superusers: a review from before the run log cannot tell which fields changed since. */}
          {isSuperuser && !!aiReviewData && !runSummary?.reviewedValues && (
            <div className={classes.legacyNote} data-testid="ai-review-legacy-note">
              <Typography variant="caption">{LEGACY_REVIEW_HINT}</Typography>
            </div>
          )}

          {!!aiReviewData && (
            <>
              {/* New per-field review format */}
              {aiReviewData.fieldReviews && aiReviewData.fieldReviews.length > 0 && (
                <>
                  {/* Summary */}
                  <div className={classes.summaryBox}>
                    <div className={classes.summaryItem}>
                      <Typography className={classes.summaryCount} style={{ color: '#4caf50' }}>
                        {approvedReviews.length}
                      </Typography>
                      <Typography className={classes.summaryLabel}>Godkända</Typography>
                    </div>
                    <div className={classes.summaryItem}>
                      <Typography className={classes.summaryCount} style={{ color: '#f44336' }}>
                        {failedReviews.length}
                      </Typography>
                      <Typography className={classes.summaryLabel}>Underkända</Typography>
                    </div>
                    <div className={classes.summaryItem}>
                      <Typography className={classes.summaryCount}>{aiReviewData.fieldReviews.length}</Typography>
                      <Typography className={classes.summaryLabel}>Totalt</Typography>
                    </div>
                  </div>

                  {/* Failed fields first */}
                  {failedReviews.length > 0 && (
                    <div className={classes.section}>
                      <div
                        className={`${classes.sectionHeader} ${classes.sectionHeaderFailed}`}
                        onClick={() => setFailedExpanded(prev => !prev)}
                        onKeyDown={e => handleKeyDown(e, () => setFailedExpanded(prev => !prev))}
                        role="button"
                        tabIndex={0}
                        aria-expanded={failedExpanded}
                      >
                        <div className={classes.sectionHeaderLeft}>
                          <ErrorIcon style={{ color: '#f44336', fontSize: 18 }} />
                          <Typography variant="subtitle2" className={classes.sectionTitle}>
                            Kräver åtgärd ({failedReviews.length})
                          </Typography>
                        </div>
                        {failedExpanded ? (
                          <ExpandLessIcon className={classes.expandIcon} />
                        ) : (
                          <ExpandMoreIcon className={classes.expandIcon} />
                        )}
                      </div>
                      <Collapse in={failedExpanded}>
                        {failedReviews.map((review, index) => (
                          <div key={index} className={`${classes.fieldReviewItem} ${classes.fieldReviewFailed}`}>
                            <div className={classes.fieldReviewContent}>
                              {renderFieldLabel(review)}
                              {review.reason && renderFieldReason(review)}
                            </div>
                          </div>
                        ))}
                      </Collapse>
                    </div>
                  )}

                  {/* Approved fields */}
                  {approvedReviews.length > 0 && (
                    <div className={classes.section}>
                      <div
                        className={`${classes.sectionHeader} ${classes.sectionHeaderApproved}`}
                        onClick={() => setApprovedExpanded(prev => !prev)}
                        onKeyDown={e => handleKeyDown(e, () => setApprovedExpanded(prev => !prev))}
                        role="button"
                        tabIndex={0}
                        aria-expanded={approvedExpanded}
                      >
                        <div className={classes.sectionHeaderLeft}>
                          <CheckCircleIcon style={{ color: '#4caf50', fontSize: 18 }} />
                          <Typography variant="subtitle2" className={classes.sectionTitle}>
                            Godkända fält ({approvedReviews.length})
                          </Typography>
                        </div>
                        {approvedExpanded ? (
                          <ExpandLessIcon className={classes.expandIcon} />
                        ) : (
                          <ExpandMoreIcon className={classes.expandIcon} />
                        )}
                      </div>
                      <Collapse in={approvedExpanded}>
                        {approvedReviews.map((review, index) => (
                          <div key={index} className={`${classes.fieldReviewItem} ${classes.fieldReviewApproved}`}>
                            <div className={classes.fieldReviewContent}>
                              {renderFieldLabel(review)}
                              {review.reason && renderFieldReason(review)}
                            </div>
                          </div>
                        ))}
                      </Collapse>
                    </div>
                  )}
                </>
              )}

              {/* Legacy format fallback */}
              {!aiReviewData.fieldReviews && aiReviewData.aiReviewIdentifiedIssues?.length && (
                <div className={classes.section}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Identifierade brister
                  </Typography>
                  <ul>
                    {aiReviewData.aiReviewIdentifiedIssues.map((rec, index) => (
                      <li key={index}>
                        <Typography>{rec}</Typography>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {!aiReviewData && (
            <Box flexGrow={1} px={1} py={3} color={isError ? '#FF0000' : undefined}>
              <Box display="flex" alignItems="center" textAlign="center" height="100%">
                {isError
                  ? errorText || 'Det gick inte att hämta AI-analysen. Vänligen försök igen genom att klicka på "Granska produkten".'
                  : 'Ingen AI-analys tillgänglig för denna produkt. Klicka på "Granska produkten" för att generera en analys.'}
              </Box>
            </Box>
          )}

          <div className={classes.refreshButton}>
            <Button
              size="medium"
              variant="contained"
              color="primary"
              fullWidth
              disableElevation
              onClick={() => handleLoadAIAnalysis('button')}
              disabled={isRefreshing || categoriesLoading}
              startIcon={
                isRefreshing ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <RefreshIcon className={classes.refreshIcon} />
                )
              }
            >
              {isRefreshing ? 'Granskar...' : 'Granska produkten'}
            </Button>
          </div>

          {isSuperuser && !!aiReviewData && !!aiReviewRunId && (
            <Button size="medium" variant="outlined" fullWidth onClick={() => setDetailsRunId(aiReviewRunId)} disabled={isRefreshing}>
              Review details
            </Button>
          )}
        </div>

        {isLoading && (
          <div className={classes.loadingContainer}>
            <DynamicText texts={LOADING_MESSAGES} isRunning={isLoading || isRefreshing} spinner />
            <Typography className={classes.loadingSubtext}>
              Granskningen tar vanligtvis 1-2 minuter.
              <br />
              Vi analyserar produkten mot gällande EU-förordningar.
            </Typography>
          </div>
        )}
      </Box>

      {isSuperuser && !!state.id && (
        <AiReviewFieldHistoryLoader productId={state.id} refreshKey={aiReviewRunId} onLoad={setFieldHistory} />
      )}

      {isSuperuser && detailsRunId && (
        <AiReviewRunDialog runId={detailsRunId} productId={state.id} onClose={() => setDetailsRunId(null)} />
      )}
    </>
  );
};

export default AIReviewer;
